import React, { useState, useEffect, useCallback } from 'react';
import { Photo, PhotoCategory } from '../types/Photo';
import mediaService from '../services/mediaService';
import { FaTrash } from 'react-icons/fa';
import './PhotoGallery.css';

// Fonction pour optimiser les URLs d'images (compatible Cloudinary si des anciennes URLs subsistent)
const optimizeCloudinaryUrl = (url: string, width: number = 800): string => {
  if (!url.includes('cloudinary.com')) return url;
  
  // Insérer les transformations Cloudinary
  const transformations = `w_${width},c_limit,f_auto,q_auto:good`;
  return url.replace('/upload/', `/upload/${transformations}/`);
};

interface PhotoGalleryProps {
  category: PhotoCategory;
  isAdmin?: boolean; // Optionnel, par défaut false
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ category, isAdmin = false }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchPhotos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mediaService.getByCategory(category);
      setPhotos(data);
    } catch (err) {
      console.error('Erreur lors de la récupération des photos:', err);
      setPhotos([]);
      setError(err instanceof Error ? err.message : 'Impossible de charger les photos');
    } finally {
      setLoading(false);
    }
  }, [category]);
  
  // Charger les photos
  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  // Gérer le téléchargement de fichier
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // Télécharger une photo
  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;

    setUploading(true);

    try {
      await mediaService.upload([selectedFile], category, description);

      // Réinitialiser le formulaire et recharger les photos
      setSelectedFile(null);
      setDescription('');
      fetchPhotos();
    } catch (err) {
      console.error('Erreur de téléchargement:', err);
      setError(err instanceof Error ? err.message : 'Erreur de téléchargement');
    } finally {
      setUploading(false);
    }
  };

  // Supprimer une photo
  const handleDelete = async (id: string) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette image ?')) return;

    try {
      await mediaService.remove(id);
      // Mettre à jour la liste des photos
      setPhotos(photos.filter(photo => photo._id.toString() !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de suppression');
    }
  };

  if (loading) return <div>Chargement...</div>;
  if (error) return <div>Erreur: {error}</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Indicateur de chargement */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-700">Chargement des photos...</span>
        </div>
      )}

      {/* Formulaire de téléchargement - visible uniquement pour les administrateurs */}
      {isAdmin && (
        <form onSubmit={handleUpload} className="mb-8 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Sélectionner une image
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="mt-1 block w-full text-sm text-gray-500
                     file:mr-4 file:py-2 file:px-4
                     file:rounded-full file:border-0
                     file:text-sm file:font-semibold
                     file:bg-violet-50 file:text-violet-700
                     hover:file:bg-violet-100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Description (optionnelle)
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm
                     focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={!selectedFile || uploading}
          className="inline-flex justify-center py-2 px-4 border border-transparent
                   shadow-sm text-sm font-medium rounded-md text-white
                   bg-indigo-600 hover:bg-indigo-700 focus:outline-none
                   focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                   disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {uploading ? 'Téléchargement...' : 'Télécharger'}
        </button>
      </form>
      )}

      {/* Message si aucune photo */}
      {!loading && photos.length === 0 && !error && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune photo</h3>
          <p className="mt-1 text-sm text-gray-500">Aucune photo n'est disponible dans cette catégorie.</p>
        </div>
      )}

      {/* Grille de photos style Instagram */}
      {!loading && photos.length > 0 && (
        <div className="instagram-gallery" id="photo-gallery">
          {photos.map((photo) => (
            <div
            key={photo._id ? photo._id.toString() : `photo-${Math.random()}`}
            className={isAdmin ? "instagram-item" : "instagram-item flex justify-center items-center"}
          >
            <img
              src={optimizeCloudinaryUrl(photo.url, 800)}
              srcSet={`
                ${optimizeCloudinaryUrl(photo.url, 400)} 400w,
                ${optimizeCloudinaryUrl(photo.url, 800)} 800w,
                ${optimizeCloudinaryUrl(photo.url, 1200)} 1200w
              `}
              sizes="(max-width: 640px) 400px, (max-width: 1024px) 800px, 1200px"
              alt={photo.description || ''}
              className={isAdmin ? "instagram-image" : "w-auto h-auto max-w-full"}
              loading="lazy"
            />
              <div className="instagram-overlay">
                {isAdmin && photo._id && (
                  <button
                    onClick={() => handleDelete(photo._id.toString())}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                    title="Supprimer cette photo"
                  >
                    <FaTrash size={16} />
                  </button>
                )}
              </div>
              
              {photo.description && (
                <div className="p-4 bg-white">
                  <p className="text-sm font-medium text-gray-900">{photo.description}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(photo.created_at).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              )}
              
              {isAdmin && photo._id && (
                <button
                  onClick={() => handleDelete(photo._id.toString())}
                  className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full
                           opacity-0 group-hover:opacity-100 transition-opacity
                           hover:bg-red-700 focus:outline-none focus:ring-2
                           focus:ring-offset-2 focus:ring-red-500"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </div>
        ))}
      </div>
      )}
    </div>
  );
};

export default PhotoGallery;
